import { useCallback, useMemo } from 'react';

import { TestCase, TestFilterFunction, TestFilters, TestReport, TestSuite } from '../TestResults.types';
import { calculateSummary, createStatusFilter } from './TestResultsExplorer.utils';

function createTextFilter(searchText: string): TestFilterFunction {
  const normalizedText = searchText.toLowerCase().trim();
  return (testCase: TestCase) =>
    !normalizedText ||
    [testCase.name, testCase.className].filter(Boolean).some((text) => text.toLowerCase().includes(normalizedText));
}

const combineFilters = (filters: TestFilterFunction[]): TestFilterFunction => {
  return (testCase: TestCase) => filters.every((filter) => filter(testCase));
};

const createFilterPredicate = (filterState: TestFilters): TestFilterFunction => {
  const filters: TestFilterFunction[] = [];

  if (filterState.search) {
    filters.push(createTextFilter(filterState.search));
  }

  if (filterState.status) {
    filters.push(createStatusFilter(filterState.status));
  }

  return filters.length ? combineFilters(filters) : () => true;
};

function filterReport(report: TestReport, predicate: (test: TestCase) => boolean): TestReport {
  if (!predicate) {
    return report;
  }

  function notNull(suite: TestSuite | null): suite is TestSuite {
    return suite !== null;
  }

  function filterSuite(suite: TestSuite): TestSuite | null {
    const testCases = suite.testCases?.filter(predicate) || [];
    const testSuites = suite.testSuites?.map((subSuite) => filterSuite(subSuite)).filter(notNull) || [];

    if (testCases.length === 0 && testSuites.length === 0) {
      return null;
    }

    return { ...suite, testCases, testSuites };
  }

  return { testSuites: report.testSuites.map((suite) => filterSuite(suite)).filter(notNull) };
}

const useFilteredReport = (report: TestReport, filterState: TestFilters) => {
  const summary = useMemo(() => calculateSummary(report), [report]);

  const filter = useCallback(
    (state: TestFilters): TestReport => {
      const predicate = createFilterPredicate(state);
      return filterReport(report, predicate);
    },
    [report],
  );

  const filteredReport = useMemo(() => filter(filterState), [filter, filterState]);

  return useMemo(
    () => ({
      summary,
      report: filteredReport,
    }),
    [summary, filteredReport],
  );
};

export default useFilteredReport;
